import { useState, useContext, useEffect, useCallback } from "react";
import '../../styles/page-titles.css';
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import { SidebarContext } from "../../AppRoutes";
import { getRelatorioGorjetas } from "../../services/api";
import { nowTz } from '../../utils/timezone';
import Loading from "react-loading";
import { FaHandHoldingUsd, FaMoneyBillWave, FaReceipt, FaCalculator } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatPriceWithSymbol } from '../../utils/currency';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 8px;
    flex-wrap: wrap;
    gap: 16px;
`;

const Subtitle = styled.p`
    color: #666;
    font-size: 14px;
    margin: 0 0 24px;
`;

const Filters = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    .presets {
        display: flex;
        gap: 6px;
        background: #f1f5f9;
        padding: 4px;
        border-radius: 10px;
        flex-wrap: wrap;
    }

    .presets button {
        padding: 8px 14px;
        background: transparent;
        color: #475569;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        transition: all 0.15s;
    }

    .presets button.ativo {
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: #fff;
        box-shadow: 0 2px 6px rgba(49, 140, 213, 0.35);
    }

    .dates {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    input[type="date"] {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
    }

    button.update {
        padding: 9px 16px;
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;

        &:hover { opacity: 0.9; }
        &:disabled { opacity: 0.6; cursor: not-allowed; }
    }

    @media (max-width: 600px) {
        width: 100%;
        .dates { width: 100%; }
        input[type="date"], button.update { flex: 1; }
    }
`;

const LoadingWrap = styled.div`
    display: flex;
    justify-content: center;
    padding: 48px 0;
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
`;

const MetricCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;
    ${({ highlight }) => highlight && `
        background: linear-gradient(135deg, rgba(40, 167, 69, 0.08), rgba(76, 217, 100, 0.12));
        border: 1px solid rgba(40, 167, 69, 0.2);
    `}

    .icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        margin-bottom: 8px;
    }

    .icon.blue { background: rgba(49, 140, 213, 0.1); color: rgb(49, 140, 213); }
    .icon.green { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .icon.orange { background: rgba(255, 152, 0, 0.1); color: #ff9800; }
    .icon.purple { background: rgba(102, 126, 234, 0.1); color: #667eea; }

    .label { font-size: 14px; color: #666; }
    .value { font-size: 28px; font-weight: 700; color: #333; }
    .sub { font-size: 12px; color: #999; }

    @media (max-width: 500px) {
        padding: 14px;
        .value { font-size: 22px; }
    }
`;

const TableCard = styled.div`
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 24px;
    overflow: hidden;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin: 0 0 20px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .table-wrap {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    @media (max-width: 600px) { padding: 16px; }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 640px;

    thead th {
        text-align: left;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #94a3b8;
        font-weight: 600;
        padding: 10px 12px;
        border-bottom: 2px solid #eef2f6;
        white-space: nowrap;
    }

    thead th.num, tbody td.num { text-align: right; }

    tbody td {
        padding: 14px 12px;
        border-bottom: 1px solid #f1f5f9;
        font-size: 14px;
        color: #1e293b;
        white-space: nowrap;
    }

    tbody tr:hover { background: #f8fafc; }

    tbody tr.unassigned td {
        color: #94a3b8;
        font-style: italic;
        background: #fafbfc;
    }

    .driver {
        display: flex;
        align-items: center;
        gap: 10px;
        font-weight: 600;
    }

    .driver .avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: rgba(49, 140, 213, 0.1);
        color: rgb(49, 140, 213);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        flex-shrink: 0;
    }

    td.fees { font-weight: 700; color: #28a745; }

    tfoot td {
        padding: 14px 12px;
        border-top: 2px solid #eef2f6;
        font-size: 14px;
        font-weight: 700;
        color: #333;
    }
    tfoot td.num { text-align: right; }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 16px;
    color: #94a3b8;
    gap: 12px;

    svg { font-size: 44px; opacity: 0.5; }
    span { font-size: 15px; }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
`;

const PRESETS = [
    { key: 'hoje', days: 0, labelKey: 'performance.today' },
    { key: '7dias', days: 6, labelKey: 'performance.last7' },
    { key: '15dias', days: 14, labelKey: 'performance.last15' },
    { key: '30dias', days: 29, labelKey: 'performance.last30' },
];

const RelatorioGorjetas = () => {
    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('reports');

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState({
        resumo: { total: 0, quantidade: 0, ticket_medio: 0, por_forma_pagamento: {} },
        por_dia: []
    });
    const [preset, setPreset] = useState('7dias');
    const [startDate, setStartDate] = useState(nowTz().subtract(6, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(nowTz().format('YYYY-MM-DD'));

    const applyPreset = (key) => {
        const found = PRESETS.find(p => p.key === key);
        if (!found) return;
        setPreset(key);
        setStartDate(nowTz().subtract(found.days, 'days').format('YYYY-MM-DD'));
        setEndDate(nowTz().format('YYYY-MM-DD'));
    };

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getRelatorioGorjetas(startDate, endDate);
            if (response.data?.success) setData(response.data);
        } catch (error) {
            console.error('[RELATORIO-GORJETAS] Erro:', error);
            toast.error(t('tips.loadError', 'Nao foi possivel carregar o relatorio de gorjetas.'));
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, t]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const resumo = data.resumo || {};
    const porForma = resumo.por_forma_pagamento || {};
    const dias = data.por_dia || [];

    return (
        <>
            <PermissionGate permissions={['relatorios']}>
                <Container sidebar={sidebar}>
                    <div className="page-title-with-subtitle">
                        <div className="page-title-with-icon">
                            <div className="page-icon"><FaHandHoldingUsd /></div>
                            <h1 className="page-title">{t('tips.title', 'Gorjetas')}</h1>
                        </div>
                        <p className="page-subtitle">
                            {t('tips.subtitle', 'Gorjetas recebidas no periodo, para o acerto com a equipe.')}
                        </p>
                    </div>

                    <Filters>
                        <div className="presets">
                            {PRESETS.map(p => (
                                <button
                                    key={p.key}
                                    className={preset === p.key ? 'active' : ''}
                                    onClick={() => applyPreset(p.key)}
                                >
                                    {t(p.labelKey)}
                                </button>
                            ))}
                        </div>
                        <div className="dates">
                            <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPreset(''); }} />
                            <span>-</span>
                            <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPreset(''); }} />
                        </div>
                    </Filters>

                    {isLoading ? (
                        <LoadingWrap><Loading type="spin" color="rgb(49,140,213)" height={48} width={48} /></LoadingWrap>
                    ) : (
                        <>
                            {/* A gorjeta NAO e faturamento: o card em destaque e o
                                valor a repassar para a equipe, nao receita da loja. */}
                            <CardsGrid>
                                <MetricCard highlight>
                                    <div className="icon"><FaMoneyBillWave /></div>
                                    <span className="label">{t('tips.totalToStaff', 'Total a repassar a equipe')}</span>
                                    <span className="value">{formatPriceWithSymbol(resumo.total || 0)}</span>
                                    <span className="hint">{t('tips.notRevenue', 'Nao conta como faturamento da loja')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon"><FaReceipt /></div>
                                    <span className="label">{t('tips.count', 'Gorjetas recebidas')}</span>
                                    <span className="value">{resumo.quantidade || 0}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon"><FaCalculator /></div>
                                    <span className="label">{t('tips.average', 'Valor medio')}</span>
                                    <span className="value">{formatPriceWithSymbol(resumo.ticket_medio || 0)}</span>
                                </MetricCard>
                            </CardsGrid>

                            <TableCard>
                                <h2>{t('tips.byPaymentMethod', 'Por forma de pagamento')}</h2>
                                <div className="table-wrapper">
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>{t('tips.method', 'Forma')}</th>
                                                <th className="num">{t('tips.amount', 'Valor')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(porForma).map(([forma, valor]) => (
                                                <tr key={forma}>
                                                    <td>{forma}</td>
                                                    <td className="num">{formatPriceWithSymbol(valor || 0)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td>{t('common.total', 'Total')}</td>
                                                <td className="num">{formatPriceWithSymbol(resumo.total || 0)}</td>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </div>
                            </TableCard>

                            <TableCard>
                                <h2>{t('tips.byDay', 'Por dia')}</h2>
                                {dias.length === 0 ? (
                                    <p className="empty">{t('tips.empty', 'Nenhuma gorjeta registrada no periodo.')}</p>
                                ) : (
                                    <div className="table-wrapper">
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th>{t('tips.day', 'Dia')}</th>
                                                    <th className="num">{t('tips.amount', 'Valor')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dias.map(d => (
                                                    <tr key={d.data}>
                                                        <td>{d.data}</td>
                                                        <td className="num">{formatPriceWithSymbol(d.valor || 0)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </TableCard>
                        </>
                    )}
                </Container>
            </PermissionGate>
        </>
    );
};

export default RelatorioGorjetas;
